import { Button, Icon, Input, Label, Select, Textarea } from '@anytag/shared-atoms';
import { Campaign } from '../../types';

interface RuleBuilderProps {
  campaigns: Campaign[];
  selectedId: string;
  activeCampaign: Campaign;
  onSelect: (id: string) => void;
  onCreate: () => void;
  onDelete: (id: string) => void;
  onUpdate: (field: keyof Campaign, value: string) => void;
}

const LANGUAGES = [
  'English',
  'Vietnamese',
  'Thai',
  'Indonesian',
  'Chinese (Simplified)',
  'Chinese (Traditional)',
  'Japanese',
  'Korean',
  'Spanish',
  'French',
  'German'
];

export const RuleBuilder = ({
  campaigns,
  selectedId,
  activeCampaign,
  onSelect,
  onCreate,
  onDelete,
  onUpdate
}: RuleBuilderProps) => (
  <div className="flex h-full flex-col rounded-xl border-base-border bg-white p-5">
    <div className="mb-4 flex shrink-0 gap-2 md:grid-cols-12">
      <div className="flex-2 md:col-span-4">
        <div className="mb-1.5 flex items-center justify-between">
          <label
            title="Active Campaign ({campaigns.length}/10)"
            className="min-w-3 flex-wrap text-xxs font-semibold tracking-wide break-all text-primary uppercase"
          >
            Active Campaign ({campaigns.length}/10)
          </label>
          <div className="flex gap-2">
            <Button
              onClick={onCreate}
              disabled={campaigns.length >= 10}
              title="New"
              size="sm"
              prefixIcon={{ icon: 'plus', size: 8 }}
              className="gap-x-0.5 px-0.5 text-xs"
              tooltip={{
                help: 'Create New Campaign',
                place: 'top'
              }}
            />
            <Button
              onClick={() => onDelete(selectedId)}
              disabled={campaigns.length <= 1}
              title="Del"
              size="sm"
              variant="solid"
              prefixIcon={{ icon: 'trash', size: 10 }}
              className="gap-x-0.5 px-0.5 text-xs"
              tooltip={{
                help: 'Delete Current Campaign',
                place: 'top'
              }}
            />
          </div>
        </div>
        <Select
          value={selectedId}
          onChange={(value) => onSelect(value)}
          options={campaigns.map((c) => ({ label: c.name, value: c.id }))}
          clearable={false}
        />
      </div>

      <div className="flex-1 self-end md:col-span-5">
        <Label title="Campaign Name" />
        <Input
          value={activeCampaign.name}
          onChange={(e) => onUpdate('name', e.target.value)}
          placeholder="Campaign Name"
        />
      </div>

      <div className="flex-1 self-end md:col-span-3">
        <Label title="Target Language" />
        <Select
          value={activeCampaign.language || 'English'}
          onChange={(value) => onUpdate('language', value)}
          options={LANGUAGES.map((lang) => ({ label: lang, value: lang }))}
          clearable={false}
        />
      </div>
    </div>

    <div className="mb-4 flex h-1/4 min-h-25 shrink-0 flex-col">
      <Label title="Campaign Context / Objective" className="font-semibold text-orange-80 uppercase" />
      <Textarea
        value={activeCampaign.context}
        onChange={(e) => onUpdate('context', e.target.value)}
        placeholder="e.g. This is a 30s social media ad highlighting key product features..."
        className="h-full resize-none"
      />
    </div>

    <div className="flex min-h-0 flex-1 flex-col gap-4 md:flex-row">
      <div className="flex h-full min-h-[150px] flex-1 flex-col">
        <Label
          title={
            <div className="flex w-full justify-between text-xs">
              <span className="font-semibold text-attention-green uppercase">Must Include (Do)</span>
              <span className="font-normal normal-case">One per line</span>
            </div>
          }
        />
        <div className="relative flex-1">
          <Icon icon="check" variant="green" size={18} className="absolute top-3 left-3 z-10" />
          <Textarea
            value={activeCampaign.doText}
            onChange={(e) => onUpdate('doText', e.target.value)}
            placeholder={`- Clear product placement\n- Smiling actors`}
            className="h-full resize-none pl-10"
          />
        </div>
      </div>

      <div className="flex h-full min-h-[150px] flex-1 flex-col">
        <Label
          title={
            <div className="flex w-full justify-between text-xs">
              <span className="font-semibold text-attention-red uppercase">Must Avoid (Don't)</span>
              <span className="font-normal normal-case">One per line</span>
            </div>
          }
        />
        <div className="relative flex-1">
          <Icon icon="close" variant="red" size={16} className="absolute top-3 left-3 z-10" />
          <Textarea
            value={activeCampaign.dontText}
            onChange={(e) => onUpdate('dontText', e.target.value)}
            placeholder={`- Watermarks\n- Blurry footage`}
            className="h-full resize-none pl-10"
          />
        </div>
      </div>
    </div>
  </div>
);
